import React from 'react';
import { motion } from 'framer-motion';
import {
  CaretRight,
  ChatText,
  Clock,
  Cpu,
  FileText,
  Lightning,
  Microphone,
  Target,
  TrendUp
} from '@phosphor-icons/react';

/**
 * OpportunityCard - Enhanced card for displaying automation opportunities
 * 
 * Shows:
 * - Title and one-liner
 * - Impact level
 * - Suggested capabilities
 * - Estimated setup time
 */
const OpportunityCard = ({ opportunity, onClick, index = 0 }) => {
  const { 
    title, 
    one_liner, 
    impact_level, 
    estimated_setup_time,
    suggested_capabilities = [],
    capability_profile
  } = opportunity;

  const impactColors = {
    high: 'text-emerald-400 bg-emerald-500/10',
    medium: 'text-amber-400 bg-amber-500/10',
    low: 'text-slate-400 bg-slate-500/10'
  };

  const profileIcons = {
    text_agent: ChatText,
    live_voice_s2s_agent: Microphone,
    voice_input_agent: Microphone,
    integration_automation_agent: Lightning
  };

  const ProfileIcon = profileIcons[capability_profile] || Cpu;

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.02, x: 4 }}
      onClick={onClick}
      className="w-full p-4 rounded-xl bg-gradient-to-br from-emerald-500/5 to-teal-500/5 
        border border-emerald-500/20 hover:border-emerald-500/40 
        text-left transition-all group"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="p-2 rounded-lg bg-emerald-500/10 flex-shrink-0">
          <Target className="w-4 h-4 text-emerald-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-white text-sm truncate group-hover:text-emerald-400 transition-colors">
              {title}
            </h4>
            <CaretRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 
              group-hover:translate-x-1 transition-all flex-shrink-0" />
          </div>
          
          <p className="text-xs text-slate-500 line-clamp-2 mb-2">
            {one_liner}
          </p>

          {/* Meta Info */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Impact Badge */}
            {impact_level && (
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${impactColors[impact_level] || impactColors.medium}`}>
                <TrendUp className="w-3 h-3" />
                {impact_level} impact
              </span>
            )}

            {/* Profile Type */}
            {capability_profile && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 text-[10px] font-medium">
                <ProfileIcon className="w-3 h-3" />
                {capability_profile.replace(/_/g, ' ').replace('agent', '').trim()}
              </span>
            )}

            {/* Setup Time */}
            {estimated_setup_time && (
              <span className="flex items-center gap-1 text-[10px] text-slate-500">
                <Clock className="w-3 h-3" />
                {estimated_setup_time}
              </span>
            )}
          </div>

          {/* Capabilities Preview */}
          {suggested_capabilities.length > 0 && (
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {suggested_capabilities.slice(0, 3).map((cap, idx) => (
                <span 
                  key={idx}
                  className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] text-slate-400"
                >
                  {cap.replace(/_/g, ' ')}
                </span>
              ))}
              {suggested_capabilities.length > 3 && (
                <span className="text-[10px] text-slate-500">
                  +{suggested_capabilities.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
};

export default OpportunityCard;
